import { Injectable } from '@angular/core';
import { SyllabusQuery, SyllabusStoreModel } from '@examdojo/category/v2';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { PayoutRateQuery } from '@examdojo/payments';
import { QueryEntity } from '@examdojo/state';
import { UserQuery } from '@examdojo/user';
import { map, of, switchMap } from 'rxjs';
import { REVIEW_PERMISSIONS } from '@examdojo/review';
import { AdminUserStoreModel, AdminUserUIModel } from './admin-user.model';
import { AdminUserState, AdminUserStore } from './admin-user.store';

@Injectable({ providedIn: 'root' })
export class AdminUserQuery extends QueryEntity<AdminUserState, AdminUserStoreModel, AdminUserUIModel, 'id'> {
  constructor(
    protected override store: AdminUserStore,
    private readonly syllabusQuery: SyllabusQuery,
    private readonly userQuery: UserQuery,
    private readonly payoutRateQuery: PayoutRateQuery,
  ) {
    super(
      store,
      () => [this.syllabusQuery.entities$, this.payoutRateQuery.entities$],
      (id) => [
        this.selectEntity(id).pipe(
          map((entity) => (entity?.assigned_syllabuses ?? []).map(({ syllabus_id }) => syllabus_id)),
          switchMap((assignedSyllabusesIds) =>
            assignedSyllabusesIds && assignedSyllabusesIds.length
              ? this.syllabusQuery.selectAllEntities({
                  filterEntity: (entity) => assignedSyllabusesIds.includes(entity.id),
                })
              : of(null),
          ),
        ),
        id ? this.payoutRateQuery.selectUserRate(id) : of(null),
      ],
      (entity: AdminUserStoreModel) => {
        const assignedSyllabusesWithAtLeastOneRoleApplied =
          entity.assigned_syllabuses?.filter((assignedSyllabus) =>
            REVIEW_PERMISSIONS.some((reviewPermission) => assignedSyllabus[reviewPermission] === true),
          ) ?? [];

        const assignedSyllabuses =
          assignedSyllabusesWithAtLeastOneRoleApplied.length > 0
            ? this.syllabusQuery.getAllUIEntities({
                filterEntity: (filterEntity: SyllabusStoreModel) =>
                  assignedSyllabusesWithAtLeastOneRoleApplied
                    .map(({ syllabus_id }) => syllabus_id)
                    .includes(filterEntity.id),
              })
            : [];

        const payoutRate = entity.id ? this.payoutRateQuery.getUserRate(entity.id)?.rate ?? null : null;

        const assignedProgramNames = [
          ...new Set(assignedSyllabuses.map((syllabus) => syllabus.program.code).filter(isNotNullish)),
        ];

        return {
          ...entity,
          assignedSyllabuses: assignedSyllabuses,
          assignedProgramNames: assignedProgramNames,
          payoutRate: payoutRate,
        };
      },
    );
  }

  readonly isCurrentUserSelected$ = this.selectActive().pipe(map((user) => user?.id === this.userQuery.getActiveId()));

  readonly isUserAdmin$ = this.selectActive().pipe(map((user) => user?.role === 'ADMIN'));
}
